import api from 'services/api';
import { array, bool, enforce, nullable, num, object, runSchema, str } from 'validation';

import { folderPathSchema, type FolderPath } from 'api/shared/folderPath';
import { recurrenceSchema, type Recurrence, type SaveRecurrenceDefinition } from './types';

// POST /recurrence_definitions — create. Backend:
// `RecurrenceDefinitionService.create` returns the `INSERT … RETURNING` row with
// `zone_name` echoed from the request (not read from the DB) + the shared
// `recurrences` / `folder_path` serializers.
//
// `CreatedRecurrenceDefinition` is its OWN type + schema, even though it matches
// the read shape today: it is produced by a different backend statement
// (equal-by-coincidence, not identity — see `../../README.md`). Structural
// typing lets it flow wherever a `RecurrenceDefinition` is expected downstream.
export interface CreatedRecurrenceDefinition {
  readonly recurrence_definition_id: number;
  readonly name: string;
  readonly description: string | null;
  readonly zone_name: string | null;
  readonly resubmit_on_error: boolean;
  readonly recurrences: readonly Recurrence[];
  readonly folder_id: number | null;
  readonly folder_path: FolderPath | null;
  readonly created_at: string;
  readonly updated_at: string;
}

const createdSchema = object<CreatedRecurrenceDefinition>({
  recurrence_definition_id: num(),
  name: str(),
  description: nullable(str()),
  zone_name: nullable(str()),
  resubmit_on_error: bool(),
  recurrences: array(recurrenceSchema),
  folder_id: nullable(num()),
  folder_path: nullable(folderPathSchema),
  created_at: str(),
  updated_at: str(),
});

/**
 * POST `/recurrence_definitions`. Returns the created record. Throws on HTTP
 * error (caller handles the 400 path) or on structural validation failure of
 * the response.
 */
export const postRecurrenceDefinition = async (save: SaveRecurrenceDefinition): Promise<CreatedRecurrenceDefinition> => {
  const res = await api.post('/recurrence_definitions', save);
  const r = runSchema(createdSchema, res.data, 'postRecurrenceDefinition');
  if (!r.ok) {
    throw new Error('postRecurrenceDefinition: response failed validation');
  }
  return enforce(r.value, r.warnings, ['recurrence_definition_id']);
};
